import React, { useState } from "react";
import { Fieldset, Field, Label, ErrorMessage } from "../../design/fieldset";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import { EducationInfo } from "../../models/EducationInfo";
import { Checkbox, CheckboxField } from "../../design/checkbox";
import { ParticipantService } from "../../services/ParticipantService";

interface Errors {
  collegeDegree?: string;
  gradDegree?: string;
}

interface Props {
  participantId: string;
  value?: EducationInfo;
  onFormSubmitted?: () => void;
}

const EducationForm: React.FC<Props> = ({
  participantId,
  value,
  onFormSubmitted,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [formState, setFormState] = useState<EducationInfo>(value ?? {});
  const [errors, setErrors] = useState<Errors>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await ParticipantService.updateEducation(participantId, formState);
    setErrors({});
    onFormSubmitted?.();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset className="space-y-4" disabled={isSaving}>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
          <CheckboxField>
            <Label htmlFor="highSchoolCompleted">High School Completed</Label>
            <Checkbox
              id="highSchoolCompleted"
              checked={formState.highSchoolCompleted || false}
              onChange={(checked) =>
                setFormState({ ...formState, highSchoolCompleted: checked })
              }
              disabled={isSaving}
            />
          </CheckboxField>
          <Field>
            <Label htmlFor="highestGradeCompleted">
              Highest Grade Completed
            </Label>
            <Input
              id="highestGradeCompleted"
              type="number"
              placeholder="e.g. 12"
              value={formState.highestGradeCompleted ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  highestGradeCompleted: Number(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="collegeYears">College Years Completed</Label>
            <Input
              id="collegeYears"
              type="number"
              placeholder="e.g. 4"
              value={formState.collegeYears ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  collegeYears: Number(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="collegeDegree">College Degree</Label>
            <Input
              id="collegeDegree"
              value={formState.collegeDegree || ""}
              placeholder="e.g. Bachelors in Science"
              onChange={(e) =>
                setFormState({ ...formState, collegeDegree: e.target.value })
              }
              disabled={isSaving}
            />
            {errors.collegeDegree && (
              <ErrorMessage>{errors.collegeDegree}</ErrorMessage>
            )}
          </Field>

          <Field>
            <Label htmlFor="gradYears">Graduate School Years</Label>
            <Input
              id="gradYears"
              type="number"
              placeholder="e.g. 2"
              value={formState.gradYears ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  gradYears: Number(e.target.value),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="gradDegree">Graduate Degree</Label>
            <Input
              id="gradDegree"
              value={formState.gradDegree || ""}
              placeholder="e.g. Masters in Science"
              onChange={(e) =>
                setFormState({ ...formState, gradDegree: e.target.value })
              }
              disabled={isSaving}
            />
            {errors.gradDegree && (
              <ErrorMessage>{errors.gradDegree}</ErrorMessage>
            )}
          </Field>

          <Field>
            <Label htmlFor="licenses">
              Professional Licenses (comma separated)
            </Label>
            <Input
              id="licenses"
              value={formState.licenses?.join(", ") || ""}
              placeholder="e.g. CPA, PMP"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  licenses: e.target.value.split(",").map((str) => str.trim()),
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="vocationalTraining">
              Vocational Training (comma separated)
            </Label>
            <Input
              id="vocationalTraining"
              value={formState.vocationalTraining?.join(", ") || ""}
              placeholder="e.g. Welding, Plumbing"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  vocationalTraining: e.target.value
                    .split(",")
                    .map((str) => str.trim()),
                })
              }
              disabled={isSaving}
            />
          </Field>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Education"}
          </Button>
        </div>
      </Fieldset>
    </form>
  );
};

export default EducationForm;
